<template>
<div class="ct-root" :style="themePageStyle">
  <nav class="ct-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.go(-1)" class="ct-back"><i class="mdi mdi-arrow-left"></i></a>
      <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" />
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">My Cart</span>
    </div>
    <button class="btn btn-sm btn-outline-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Browse</button>
  </div></nav>

  <div class="container ct-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <div v-else-if="!groups.length" class="text-center py-5">
      <i class="mdi mdi-cart-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Your cart is empty</h5>
      <p style="color:var(--fm-s,#6B6356);">Browse the marketplace and add animals or products to your cart. Items from different sellers are checked out separately.</p>
      <button class="btn btn-warning mt-2" @click="$router.push('/marketplace')"><i class="mdi mdi-magnify me-1"></i>Find Animals</button>
    </div>

    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 style="color:var(--fm-h,#F5F0E8);font-weight:700;margin:0;">{{ itemCount }} item{{ itemCount===1?'':'s' }} from {{ groups.length }} seller{{ groups.length===1?'':'s' }}</h5>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;">Each seller is paid through their own escrow — you'll go through checkout once per seller.</div>
        </div>
        <button class="btn btn-sm btn-outline-danger" @click="clearAll" :disabled="busy"><i class="mdi mdi-delete-sweep me-1"></i>Clear All</button>
      </div>

      <div v-for="g in groups" :key="g.seller_id" class="ct-group mb-3">
        <div class="ct-group-hd">
          <div class="d-flex align-items-center gap-2" style="min-width:0;">
            <div class="ct-avatar"><img v-if="g.seller_avatar" :src="imgUrl(g.seller_avatar)" /><span v-else>{{ (g.seller_name||'S').charAt(0) }}</span></div>
            <div style="min-width:0;">
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:14px;">{{ g.seller_name }}</div>
              <div v-if="g.seller_phone" style="color:var(--fm-s,#6B6356);font-size:11px;"><i class="mdi mdi-phone me-1"></i>{{ g.seller_phone }}</div>
            </div>
          </div>
          <div class="text-end">
            <div style="color:#BFA164;font-weight:700;font-size:15px;">KES {{ fNum(g.subtotal) }}</div>
            <div style="color:var(--fm-s,#6B6356);font-size:10px;">subtotal · {{ g.items.length }} item{{ g.items.length===1?'':'s' }}</div>
          </div>
        </div>

        <template v-for="it in g.items" :key="it.cart_item_id">
        <div class="ct-item">
          <div class="ct-item-img">
            <img v-if="it.image_url" :src="imgUrl(it.image_url)" />
            <div v-else class="ct-item-noimg"><i :class="it.item_type==='listing' ? 'mdi mdi-cow' : 'mdi mdi-package-variant'"></i></div>
          </div>
          <div class="flex-fill" style="min-width:0;">
            <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ it.title }}</div>
            <div style="color:var(--fm-m,#A09888);font-size:11px;">
              <span v-if="it.species_name">{{ it.species_name }}<span v-if="it.breed_name"> · {{ it.breed_name }}</span></span>
              <span v-if="it.item_type==='listing' && it.listing_type==='batch'" style="background:rgba(191,161,100,.1);color:#BFA164;padding:1px 6px;border-radius:4px;margin-left:6px;font-size:10px;">Batch</span>
              <span v-if="it.listing_status && it.listing_status!=='active'" style="background:rgba(255,107,107,.1);color:#FF6B6B;padding:1px 6px;border-radius:4px;margin-left:6px;font-size:10px;">{{ it.listing_status.replace(/_/g,' ') }}</span>
            </div>
            <div style="color:#BFA164;font-weight:600;font-size:13px;margin-top:4px;">KES {{ fNum(it.unit_price) }} <span v-if="it.price_type && it.price_type!=='fixed'" style="color:var(--fm-s,#6B6356);font-weight:400;font-size:10px;">{{ it.price_type }}</span></div>
          </div>
          <div class="ct-qty" v-if="canEditQuantity(it)">
            <!-- Number() coercion is load-bearing: mysqli returns quantity as a string,
                 so "1" + 1 concatenated to 11 and the >= guard compared lexicographically. -->
            <button @click="updateQty(it, Number(it.quantity) - 1)" :disabled="busy"><i class="mdi mdi-minus"></i></button>
            <span>{{ it.quantity }}</span>
            <button @click="updateQty(it, Number(it.quantity) + 1)" :disabled="busy || (it.available_quantity && Number(it.quantity) >= Number(it.available_quantity))"><i class="mdi mdi-plus"></i></button>
          </div>
          <div class="text-end" style="min-width:90px;">
            <div style="color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;">KES {{ fNum(Number(it.unit_price) * Number(it.quantity)) }}</div>
            <button class="btn btn-sm btn-link" style="color:#FF6B6B;padding:0;font-size:11px;" @click="removeItem(it)" :disabled="busy"><i class="mdi mdi-close-circle"></i> Remove</button>
          </div>
        </div>

        <!-- Insurance toggle (per livestock listing line) -->
        <div v-if="it.item_type==='listing' && it.species_id" class="ct-insure">
          <label class="ct-insure-label" :class="{checked: insuredMap[it.listing_id]}">
            <input type="checkbox"
                   :checked="!!insuredMap[it.listing_id]"
                   @change="toggleInsurance(it, $event.target.checked)"
                   :disabled="busy || (insuranceQuotes[it.listing_id] && insuranceQuotes[it.listing_id].available === false)" />
            <i class="mdi mdi-shield-check" style="color:#50C878;"></i>
            <span v-if="insuranceQuotes[it.listing_id] && insuranceQuotes[it.listing_id].available">
              <strong>Protect this animal</strong> · {{ insuranceQuotes[it.listing_id].providerShort || insuranceQuotes[it.listing_id].providerName }}
              <span style="color:#BFA164;">+ KES {{ fNum(insuranceQuotes[it.listing_id].premium) }}</span>
              <span style="color:var(--fm-s,#6B6356);font-size:10px;">({{ insuranceQuotes[it.listing_id].durationMonths }}mo cover)</span>
            </span>
            <span v-else-if="insuranceQuotes[it.listing_id] && insuranceQuotes[it.listing_id].available === false" style="color:var(--fm-s,#6B6356);">
              <i class="mdi mdi-information-outline me-1" style="color:#FF9800;"></i>
              {{ insuranceQuotes[it.listing_id].message || 'No livestock insurance product matches this animal' }}
            </span>
            <span v-else style="color:var(--fm-m,#A09888);">
              <strong>Protect this animal</strong> with livestock insurance
              <span v-if="quoteLoadingMap[it.listing_id]" class="spinner-border spinner-border-sm ms-1"></span>
            </span>
          </label>
        </div>
        </template>

      </div>

      <!-- Grand total + Unified Checkout -->
      <div class="ct-grand">
        <div v-if="totalInsurance > 0" style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;font-size:13px;">
          <span style="color:var(--fm-m,#A09888);"><i class="mdi mdi-shield-check me-1" style="color:#50C878;"></i>Insurance premiums</span>
          <span style="color:#50C878;font-weight:600;">KES {{ fNum(totalInsurance) }}</span>
        </div>
        <div style="color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:.5px;">Grand total</div>
        <div style="color:#BFA164;font-weight:700;font-size:28px;">KES {{ fNum(grandTotal) }}</div>
        <div style="color:var(--fm-s,#6B6356);font-size:12px;margin:6px 0 16px;line-height:1.5;">
          One M-Pesa payment covers everything in your cart<span v-if="totalInsurance>0"> &mdash; including livestock insurance</span>.<br>
          Flockr arranges transport from each seller — invoiced separately.
        </div>
        <button class="btn btn-warning btn-lg w-100" @click="goCheckout" :disabled="busy">
          <i class="mdi mdi-cellphone-wireless me-1"></i>Proceed to Checkout — KES {{ fNum(grandTotal) }}
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .ct-root{background:#F7F5F2!important}
html[data-theme="light"] .ct-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ct-group,.ct-grand{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ct-item{border-bottom-color:#F0EBE4!important}
</style>

<style scoped>
.ct-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ct-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ct-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:4px;text-decoration:none}
.ct-container{padding-top:80px;padding-bottom:60px;max-width:900px}
.ct-group{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;overflow:hidden}
.ct-group-hd{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;background:var(--fm-bg2,#0F0F0F);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ct-avatar{width:36px;height:36px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-weight:700;color:#BFA164;flex-shrink:0;overflow:hidden}
.ct-avatar img{width:100%;height:100%;object-fit:cover}
.ct-item{display:flex;align-items:center;gap:14px;padding:14px 18px;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.ct-item:last-child{border-bottom:none}
.ct-item-img{width:64px;height:64px;border-radius:8px;background:var(--fm-bg2,#0F0F0F);display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0}
.ct-item-img img{width:100%;height:100%;object-fit:cover}
.ct-item-noimg{color:var(--fm-s,#3A3530);font-size:24px}
.ct-qty{display:flex;align-items:center;gap:6px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;padding:2px}
.ct-qty button{width:28px;height:28px;background:transparent;border:none;color:#BFA164;cursor:pointer;display:flex;align-items:center;justify-content:center;border-radius:4px}
.ct-qty button:hover:not(:disabled){background:rgba(191,161,100,.12)}
.ct-qty button:disabled{opacity:.3;cursor:not-allowed}
.ct-qty span{min-width:28px;text-align:center;color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px}
.ct-group-ft{padding:14px 18px;border-top:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F)}
.ct-grand{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px 22px;text-align:right;margin-top:24px}

/* Per-line insurance toggle */
.ct-insure{padding:8px 18px 14px;border-bottom:1px solid var(--fm-br,#1A1A1A);background:var(--fm-bg2,#0F0F0F)}
.ct-insure-label{display:flex;align-items:center;gap:8px;cursor:pointer;font-size:12px;color:var(--fm-m,#A09888);padding:6px 10px;border-radius:6px;border:1px dashed transparent;margin:0;line-height:1.4}
.ct-insure-label:hover{border-color:rgba(80,200,120,.35);background:rgba(80,200,120,.04)}
.ct-insure-label.checked{border-color:rgba(80,200,120,.45);background:rgba(80,200,120,.06);color:var(--fm-h,#F5F0E8)}
.ct-insure-label input[type="checkbox"]{accent-color:#50C878;width:16px;height:16px;cursor:pointer}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .ct-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .ct-card { padding: 12px !important; border-radius: 10px !important; }
  .ct-insure { padding: 6px 12px 10px; }
  .ct-insure-label { font-size: 11px; padding: 5px 8px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";
export default {
  name: "Cart",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,busy:false,
    groups:[], total:0, itemCount:0, items:[],
    // Per-listing insurance state. insuredMap[listingId] = true if checkbox ticked.
    // insuranceQuotes[listingId] = { available, premium, productName, ... } once fetched.
    insuredMap: {}, insuranceQuotes: {}, quoteLoadingMap: {}
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    totalInsurance(){
      var sum = 0; var qs = this.insuranceQuotes; var insured = this.insuredMap;
      for (var lid in insured) {
        if (insured[lid] && qs[lid] && qs[lid].available) sum += Number(qs[lid].premium || 0);
      }
      return sum;
    },
    grandTotal(){ return Number(this.total||0) + this.totalInsurance; }
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.load();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect()},
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    fNum(n){return Number(n||0).toLocaleString()},
    imgUrl(u){
      // image_url from getCart already begins with "uploads/" — strip it before prepending
      // the api base, otherwise we end up with .../uploads/uploads/animals/... and a 404.
      if(!u) return '';
      if(u.indexOf('http') === 0) return u;
      return uploads + String(u).replace(/^uploads\//, '');
    },
    canEditQuantity(it){
      // Single animals can't be quantified beyond 1. Batch livestock and any
      // livestock_product listing (honey, eggs, milk, hides…) are quantifiable —
      // those come with an available_quantity > 1.
      if(it.item_type==='listing') return it.listing_type === 'batch' || it.listing_kind === 'livestock_product';
      return true;
    },
    async load(){
      this.loading=true;
      var userId=sessionStorage.getItem('userId');
      if(!userId){this.$router.push('/login');return}
      try{
        var r=await executeJson({function:'getCart',userId:userId},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.items=r.data.data.items||[];
          this.groups=r.data.data.groups||[];
          this.total=Number(r.data.data.total||0);
          this.itemCount=Number(r.data.data.itemCount||0);
        }
      }catch(e){console.error(e)}
      this.loading=false;

      // Pre-fetch insurance quotes for every livestock listing so the buyer sees
      // the premium (or the "not available" reason) up-front, without having to
      // tick the checkbox first. Done after the spinner clears so it doesn't hold
      // up the cart render. Fire-and-forget; quotes update reactively as they land.
      var self = this;
      this.items.forEach(function(it){
        if (it.item_type === 'listing' && it.species_id && !self.insuranceQuotes[it.listing_id]) {
          self.fetchQuote(it);
        }
      });
    },
    async updateQty(it, newQty){
      if(newQty < 1){return this.removeItem(it)}
      if(it.available_quantity && newQty > it.available_quantity){
        alert('Only ' + it.available_quantity + ' available'); return;
      }
      this.busy=true;
      var userId=sessionStorage.getItem('userId');
      try{
        await executeJson({function:'updateCartQuantity',userId:userId,cartItemId:it.cart_item_id,quantity:newQty},flocker);
        await this.load();
      }catch(e){alert('Failed to update quantity')}
      this.busy=false;
    },
    async removeItem(it){
      this.busy=true;
      var userId=sessionStorage.getItem('userId');
      try{
        await executeJson({function:'removeFromCart',userId:userId,cartItemId:it.cart_item_id},flocker);
        await this.load();
      }catch(e){alert('Failed to remove item')}
      this.busy=false;
    },
    async clearAll(){
      if(!confirm('Empty your entire cart? This cannot be undone.'))return;
      this.busy=true;
      var userId=sessionStorage.getItem('userId');
      try{
        await executeJson({function:'clearCart',userId:userId},flocker);
        await this.load();
      }catch(e){alert('Failed to clear cart')}
      this.busy=false;
    },
    async toggleInsurance(it, checked){
      var lid = it.listing_id;
      this.insuredMap[lid] = !!checked;
      // If checked and we don't have a quote yet, fetch one
      if (checked && !this.insuranceQuotes[lid]) {
        await this.fetchQuote(it);
      }
    },
    async fetchQuote(it){
      var lid = it.listing_id;
      this.quoteLoadingMap[lid] = true;
      try {
        var r = await executeJson({
          function:'getInsuranceQuoteForListing',
          userId: sessionStorage.getItem('userId'),
          listingId: lid,
          quantity: it.quantity
        }, flocker);
        if (r.data && r.data.success) {
          var data = r.data.data || { available:false };
          // Capture the server's human-readable reason so the UI can explain WHY a quote
          // isn't available (e.g., "No livestock insurance product matches this animal").
          data.message = r.data.message || '';
          this.insuranceQuotes[lid] = data;
          if (!data.available) this.insuredMap[lid] = false;
        }
      } catch(e){
        this.insuranceQuotes[lid] = { available:false, message:'Could not check insurance availability — please try again' };
        this.insuredMap[lid] = false;
      }
      this.quoteLoadingMap[lid] = false;
    },
    goCheckout(){
      // Stash the buyer's insurance choices so CartCheckout can hand them to the server.
      // Keep this minimal — CartCheckout reads it on mount, server is the source of truth
      // for the actual premium computation.
      var insureIds = [];
      for (var lid in this.insuredMap) {
        if (this.insuredMap[lid] && this.insuranceQuotes[lid] && this.insuranceQuotes[lid].available) {
          insureIds.push(lid);
        }
      }
      sessionStorage.setItem('flockr_cart_insure_ids', JSON.stringify(insureIds));
      sessionStorage.setItem('flockr_cart_insure_quotes', JSON.stringify(this.insuranceQuotes));
      this.$router.push('/cart/checkout');
    }
  }
};
</script>
